import { useDateFormat } from 'hooks/useDateFormat'
import { useTranslation } from 'hooks/useTranslation'
import {
  Carriers,
  isPreparingShipmentOrderEvent,
  isShippedOrderEvent,
  isStatusEvent,
  Order,
  OrderShippedEvent,
  OrderStatus,
} from 'models/Order'
import { useMemo } from 'react'
import { formatCurrency } from 'utils/currency'
import { Step } from '../components/Step'
import { orderStatusMap } from '../utils/orderStatusMap'

export const getTrackingLink = (details?: OrderShippedEvent) => {
  if (!details) return ''
  if (details.trackingLink) return details.trackingLink

  switch (details.carrier.toLowerCase()) {
    case Carriers.FEDEX:
      return `https://www.fedex.com/fedextrack/?trknbr=${details.trackingNumber}`
    case Carriers.UPS:
      return `https://www.ups.com/track?tracknum=${details.trackingNumber}/trackdetails`
    default:
      return ''
  }
}

export const useOrderSteps = (order?: Order) => {
  const { t } = useTranslation()
  const { format } = useDateFormat()

  const preparingShipmentDetails = useMemo(() => {
    return order?.historic.events.find(isPreparingShipmentOrderEvent)
  }, [order?.historic.events])

  const shippedDetails = useMemo(() => {
    return order?.historic.events.find(isShippedOrderEvent)
  }, [order?.historic.events])

  const deliveredDetails = useMemo(() => {
    return order?.historic.events.find((event) => isStatusEvent(event, OrderStatus.DELIVERED))
  }, [order?.historic.events])

  if (!order) return []

  const createStep = (
    <Step
      title={t('my-account:order.status.create')}
      keyValuePairs={[
        {
          key: t('my-account:order.order-date'),
          values: [format(order.paymentDetails.paymentDate, 'MONTH_DAY_YEAR')],
        },
        {
          key: t('my-account:order.price'),
          values: [formatCurrency(order.paymentDetails.priceBreakdown.total)],
        },
        {
          key: t('my-account:order.payment-method'),
          values: [
            order.paymentDetails.cardLast4Digits
              ? `Card ending in ${order.paymentDetails.cardLast4Digits}`
              : '',
          ],
        },
      ]}
    />
  )

  const preparingShipmentStep = (
    <Step
      title={t('my-account:order.status.preparing')}
      inactive={orderStatusMap[order.status] < orderStatusMap[OrderStatus.PREPARING_SHIPMENT]}
      keyValuePairs={[
        {
          key: t('my-account:order.estimated-ship-date'),
          values: [
            preparingShipmentDetails?.estimatedShipDate
              ? format(preparingShipmentDetails.estimatedShipDate, 'MONTH_DAY_YEAR')
              : '--/--/--',
          ],
        },
        {
          key: t('my-account:order.shipping-address'),
          values: [
            order.shippingAddress.street,
            order.shippingAddress.apartment,
            `${order.shippingAddress.city}, ${order.shippingAddress.state}, ${order.shippingAddress.zipCode}`,
          ],
        },
      ]}
    />
  )

  const shippedStep = (
    <Step
      title={t('my-account:order.status.shipped')}
      inactive={orderStatusMap[order.status] < orderStatusMap[OrderStatus.SHIPPED]}
      keyValuePairs={[
        {
          key: t('my-account:order.carrier'),
          values: [shippedDetails?.carrier ?? '-'],
        },
        {
          key: t('my-account:order.tracking'),
          links: [getTrackingLink(shippedDetails)],
          values: [shippedDetails?.trackingNumber ?? '-'],
        },
      ]}
    />
  )

  const deliveredStep = (
    <Step
      inactive={orderStatusMap[order.status] < orderStatusMap[OrderStatus.DELIVERED]}
      title={t('my-account:order.status.delivered')}
      keyValuePairs={[
        {
          key: t('my-account:order.delivery-date'),
          values: [
            deliveredDetails?.occurredAt
              ? format(deliveredDetails.occurredAt, 'MONTH_DAY_YEAR')
              : '--/--/--',
          ],
        },
      ]}
    />
  )

  return [createStep, preparingShipmentStep, shippedStep, deliveredStep]
}
